import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import to from 'await-to-js'
import { useForm } from 'react-hook-form'
import { TextInput } from 'components/FormUtils/TextInput'
import FbButton from 'components/FbUI/FbButton'
import { FbLink } from 'components/FbUI/FbLink'
import { useAuth } from 'context/authentication/useAuth'
import { handleFormError } from 'utils/handleFormError'
import { HiOutlineExclamationCircle } from 'react-icons/hi'
import { useState } from 'react'
import { useCooldownTimer } from 'hooks/useCooldownTimer'

const resendEmailFormShape = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
})

function Failed() {
  const { resendVerify } = useAuth()
  const [emailSent, setEmailSent] = useState(false)
  const { cooldownSeconds, startCooldown, isOnCooldown } = useCooldownTimer()

  const {
    control,
    handleSubmit,
    setError,
    formState: { isSubmitting },
    getValues,
  } = useForm({
    defaultValues: {
      email: '',
    },
    resolver: zodResolver(resendEmailFormShape),
  })

  const onSubmit = handleSubmit(
    async (values) => {
      const [err] = await to(resendVerify(values.email))
      if (!err) {
        setEmailSent(true)
        startCooldown()
      }
      handleFormError(err, setError)
    },
    (errors) => console.warn(errors)
  )

  const handleResend = async () => {
    const email = getValues('email')
    if (!email) return

    const [err] = await to(resendVerify(email))
    if (!err) {
      startCooldown()
    }
    handleFormError(err, setError)
  }

  // Success state - email has been resent
  if (emailSent) {
    return (
      <div className="w-96 flex-shrink-0">
        <div className="text-[22px] font-light text-new-company-gray-500">
          Welcome to{' '}
          <span className="text-new-company-green-dark font-semibold">
            First Bite
          </span>
        </div>

        <div className="mt-8 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-new-company-pistachio-light flex items-center justify-center mb-4">
            <HiOutlineExclamationCircle className="w-10 h-10 text-new-company-green-dark" />
          </div>

          <h1 className="text-[20px] font-semibold mb-2">Check your inbox</h1>

          <p className="text-gray-600 mb-6">
            We&apos;ve sent a new verification email. Please click the link in
            the email to verify your account.
          </p>

          <div className="w-full p-4 bg-gray-50 rounded-lg mb-6">
            <p className="text-sm text-gray-600">
              <span className="font-medium">Tip:</span> If you don&apos;t see
              the email, check your spam or junk folder.
            </p>
          </div>

          <div className="flex flex-col gap-3 w-full">
            <FbButton
              variant="secondary"
              onClick={handleResend}
              disabled={isOnCooldown}
              className="w-full"
            >
              {isOnCooldown
                ? `Resend available in ${cooldownSeconds}s`
                : "Didn't receive it? Resend"}
            </FbButton>

            <FbLink to="/login" className="text-center">
              Back to login
            </FbLink>
          </div>
        </div>
      </div>
    )
  }

  // Form state - enter email to resend
  return (
    <div className="w-96 flex-shrink-0">
      <div className="text-[22px] font-light text-new-company-gray-500">
        Welcome to{' '}
        <span className="text-new-company-green-dark font-semibold">
          First Bite
        </span>
      </div>

      <div className="mt-8 flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
          <HiOutlineExclamationCircle className="w-10 h-10 text-red-500" />
        </div>

        <h1 className="text-[20px] font-semibold mb-2">Verification failed</h1>

        <p className="text-gray-600 mb-6">
          The verification link is invalid or has expired. Enter your email
          below to receive a new verification link.
        </p>
      </div>

      <form onSubmit={onSubmit}>
        <TextInput control={control} name="email" label="Email" />
        <FbButton type="submit" loading={isSubmitting} className="w-full mt-4">
          Resend Verification Email
        </FbButton>
      </form>

      <div className="mt-4 text-center">
        <FbLink to="/login">Back to login</FbLink>
      </div>
    </div>
  )
}

export default Failed
